import { mutate } from 'swr';
import axios, { AxiosResponse } from 'axios';
import { Notistack } from 'app/shared/components/Notistack/Notistack';
import { SERVICES } from 'app/shared/constants/apiConfig';
import { getError } from '@shared/utils/helpers';
import { IEditRetail, IProduct } from '../../interfaces';
import { SelectedRetails } from './EditProducts/EditProductModal';
import { getPostDataToSaveEditProducts, getPostDataToSaveGetPlans } from './queriesUtils';
import { ICancellationRequest, ICustomData, IVoidResponse, Plans } from './interfaces';

const getMutateProductsTable = (dealId: string) => `/${SERVICES.PRODUCT}/deals/${dealId}/dealproducts/summary`;

export const requestUpdateProduct = (product: Partial<IEditRetail>, dealId: string) => {
  return mutate(getMutateProductsTable(dealId), async() => {
    try {
      const data = {
        newRetail: product.retail,
        newMarkUp: product.markup,
        reason: product.reason || '',
      };

      await axios.put(`${SERVICES.PRODUCT}/deals/${dealId}/dealproducts/${product.dealProductId}/retail`, data);
      Notistack.successMessage('Product - Updated Successfully');
    } catch (err) {
      const error = getError(err);
      Notistack.generalError(error);
    }
  });
};

export const voidProduct = (dealProductId: string, dealId: string) => {
  return mutate(getMutateProductsTable(dealId), async() => {
    try {
      const resp = await axios.post<IVoidResponse>(`${SERVICES.PRODUCT}/deals/${dealId}/dealproducts/${dealProductId}/void`);

      if (!resp?.data?.isSuccess) {
        Notistack.generalError(resp?.data?.errorMessage ?? '');

        return;
      }
      Notistack.successMessage('Product - Void Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const voidAllProduct = (dealId: string, products?: IProduct[]) => {
  const productsIds = products?.map(({ dealProductId, productTypeName }: IProduct) => ({
    dealProductId,
    productTypeName,
  }));
  return mutate(getMutateProductsTable(dealId), async() => {
    try {
      const resp = await axios.post<IVoidResponse[]>(`${SERVICES.PRODUCT}/deals/${dealId}/dealproducts/void`, { dealProduct: productsIds });
      resp?.data.forEach((item) => {
        const productName = item?.productTypeName ?? '';

        if (item?.isSuccess) {
          Notistack.successMessage(`${productName}- Void Successfully`);
        } else {
          Notistack.generalError(`${productName ? `${productName}-` : ''} ${item?.errorMessage}`);
        }
      });
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const deleteProduct = (dealProductId: string, dealId: string) => {
  return mutate(getMutateProductsTable(dealId), async() => {
    try {
      await axios.delete(`${SERVICES.PRODUCT}/deals/${dealId}/dealproducts/${dealProductId}`);
      Notistack.successMessage('Product - Deleted Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const saveProductsPlan = (products: SelectedRetails, dealId: string, customData?: ICustomData) => {
  return mutate(getMutateProductsTable(dealId), async() => {

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dataSet: any = {
        editProducts: getPostDataToSaveEditProducts(products, dealId),
        dealCalculator: getPostDataToSaveGetPlans(products, customData),
      };
      const dataSetKey = customData?.type || 'editProducts';
      const url = customData?.submitUrl ? customData.submitUrl : `/${SERVICES.PRODUCT}/deals/${dealId}/dealproducts`;
      
      const productListMap: Record<string, string> = {
        editProducts: 'dealProducts',
        dealCalculator: 'dealCalculatorLeadProductDtos',
      };
      
      const productListKey = productListMap[dataSetKey];
      
      if (productListKey && dataSet[dataSetKey]?.[productListKey]?.length > 0) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataSet[dataSetKey][productListKey].forEach((dataItem: any) => {
          if (dataItem.maxAllowedRetail && dataItem.retail > dataItem.maxAllowedRetail) {
            dataItem.retail = dataItem.maxAllowedRetail;
            dataItem.markup = dataItem.maxAllowedRetail - dataItem.cost;
          }
        });
      }
      const result = await axios.post(url, dataSet[dataSetKey]);
      customData?.data.setLeadProductId(result.data[0]?.dealCalculatorLeadProductId);
      Notistack.successMessage('Products saved successfully.');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const updateCalculatorProduct = async(product: Pick<IEditRetail, 'retail' | 'markup' | 'reason'>, leadId: string, dealCalculatorLeadProductId: string) => {
  try {
    const data = {
      newRetail: product.retail,
      newMarkUp: product.markup,
      reason: product.reason || '',
    };

    await axios.put(`${SERVICES.PRODUCT}/leads/${leadId}/dealcalculator/${dealCalculatorLeadProductId}/retail`, data);
    Notistack.successMessage('Product - Updated Successfully');
  } catch (err) {
    const error = getError(err);
    Notistack.generalError(error);
  }
};

export const getPlan = async (getTreeUrl: string, dealId: string, isNewVersion: boolean, state: string | null | undefined, forceVendorRepull: boolean | null | undefined) => {
  try {
    const resp: AxiosResponse<Plans> = await axios.get(
      getTreeUrl
        ? `${getTreeUrl}&newVersion=${isNewVersion}&&state=${state}&forceVendorRepull=${forceVendorRepull ? true : false}`
        : `/${SERVICES.PRODUCT}/deals/${dealId}/plans?newVersion=${isNewVersion}&&state=${state}&forceVendorRepull=${forceVendorRepull ? true : false}`
    );
    return resp.data;
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const requestUpdateCancellationRequest = (cancellationRequestPayload: ICancellationRequest, dealId: string, dealProductId: string) => {
  return mutate(getMutateProductsTable(dealId), async() => {
    try {
      await axios.put(`${SERVICES.PRODUCT}/deals/${dealId}/dealproducts/${dealProductId}/cancellationrequest`, cancellationRequestPayload);
      Notistack.successMessage('Product - Updated Successfully');
    } catch (err) {
      const error = getError(err);
      Notistack.generalError(error);
    }
  });
};

